import {
  eachYearOfInterval,
  getYear,
  isValid,
  isWithinInterval,
  setYear
} from "date-fns"
import { isDate } from "lodash"
import { useEffect, useRef, useState } from "react"
import DatePicker from "react-datepicker"
import { ChangeInputType } from "../../../api/EventTypes"

import { useAppSelector } from "../../../app/hooks"
import { ErrorText } from "../../../common/errorText"
import { InputBlurEvent } from "../../../common/eventTypes"
import { keyCodes } from "../../../common/keyCodes"
import useKeyboard from "../../../hooks/useKeyboard"
import * as Utils from "../../Utils"
import Tooltip from "../tooltip"
import { IBaseFieldProperties } from "./interfaces"
export interface IDateFieldProperties extends IBaseFieldProperties {
  min?: Date | null | undefined
  max?: Date | null | undefined
  displayFormat?: string
  value?: string | number | Date
  styles?: string
  formatDateVal?: boolean
  disabled?: boolean
  showIcon?: boolean
  dropdownId?: string
  autoFocus?: boolean
  placement?: string
  required?: boolean
  isEmpty?: boolean
  hasError?: boolean
  dateValuesStartDate?: string
  dateValuesEndDate?: string
  dateHasErrText?: string
  dateCanBeNull?: boolean
  onSelectDate?: (newDate: string | number | Date) => void
}

const DatePickerField = (props: IDateFieldProperties) => {
  const { useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}
  const dateRef = useRef(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    const hasError = props.touched && (props.isEmpty || props.hasError)

    if (hasError) {
      setError(true)
    }
  }, [error, props.hasError, props.isEmpty, props.touched])

  const handleDateChange = (newDate: Date) => {
    if (!props.dateCanBeNull) {
      if (newDate === null) {
        setError(true)
      } else {
        setError(false)
      }
    } else {
      setError(true)
    }

    let _value = newDate
      ? props.formatDateVal
        ? Utils.formatDate(newDate)
        : newDate.toString()
      : null

    const dateError = _value === null && props.required
    props.onChange(props.name, _value, dateError)
  }

  const handleDateChangeRaw = (e: ChangeInputType) => {
    // check if written date is valid in min max period if not valid in current year
    const dateVal = e.target.value

    if (dateVal && dateVal.length >= 4 && props.min && props.max) {
      const currentYear = new Date().getFullYear()
      const rawDate = new Date(e.target.value)
      const dateInCurrentYear = setYear(rawDate, currentYear)
      const currentYearDateIsValidInPeriod = isWithinInterval(
        dateInCurrentYear,
        {
          start: props.min,
          end: props.max
        }
      )

      if (isValid(dateInCurrentYear) && !currentYearDateIsValidInPeriod) {
        const otherYear = eachYearOfInterval({
          start: props.min,
          end: props.max
        })
          .map((yearDate) => getYear(yearDate))
          .filter((yearString) => yearString !== currentYear)[0]

        const dateInOtherYear = setYear(rawDate, otherYear)
        const dateInOtherYearIsValidInPeriod = isWithinInterval(
          dateInOtherYear,
          {
            start: props.min,
            end: props.max
          }
        )

        if (dateInOtherYearIsValidInPeriod) {
          handleDateChange(dateInOtherYear)
        }
      }
    } else {
      handleDateChange(null)
    }
  }

  const handleBlur = (e: InputBlurEvent) => {
    const { value } = e.target
    handleSelectDate(props.value)

    if (value === "" && props.required) {
      setError(true)
    }
  }

  const handleSelectDate = (newDate: string | number | Date) => {
    let _value = newDate
      ? props.formatDateVal && isDate(newDate)
        ? Utils.formatDate(newDate)
        : newDate.toString()
      : null
    props.onSelectDate && props.onSelectDate(_value)
  }

  const showTooltip =
    (error && props.required && props.isEmpty) ||
    (error && props.required && props.isEmpty === undefined)
  const onTabFocus = () => {
    dateRef.current && dateRef.current.setOpen(false)
  }

  useKeyboard(() => onTabFocus(), [keyCodes.tab])
  return (
    <div
      className={`ocs-field-container pos-rel ${
        showTooltip ? "invalid-ocs-field" : ""
      }`}
    >
      <div className="d-flex">
        {props.caption && <label htmlFor={props.name}>{props.caption}</label>}
        {showTooltip && (
          <Tooltip tooltipAbsolute={props.readonly}>
            {props.dateHasErrText ? props.dateHasErrText : ErrorText.noDate}
          </Tooltip>
        )}
      </div>
      {props.readonly ? (
        <p
          className={
            "ocs-field-readonly" +
            (props.hasChanges
              ? useLocalTime
                ? " has-changes local-time"
                : " has-changes"
              : "")
          }
        >
          {props.value
            ? Utils.formatDateShort(new Date(props.value), props.readonly)
            : ""}
        </p>
      ) : (
        <DatePicker
          popperPlacement={props.placement && props.placement}
          ref={dateRef}
          name={props.name}
          className="ocs-field ocs-date-field "
          calendarClassName="ocs-date-picker"
          selected={props.value ? new Date(props.value) : null}
          onChange={handleDateChange}
          onChangeRaw={handleDateChangeRaw}
          isClearable={!props.required}
          dateFormat={props.displayFormat}
          minDate={props.min}
          maxDate={props.max}
          todayButton="Today"
          showPopperArrow={false}
          calendarStartDay={1}
          onBlur={handleBlur}
          onSelect={handleSelectDate}
          touched={props.touched}
          autoComplete="off"
          placeholderText={props.placeholder}
        />
      )}
    </div>
  )
}

export default DatePickerField
